import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "./Dashboard";
import { Modal, Field, Th, Td } from "./Customers";
import { Plus, FileText, Calculator, Banknote, Receipt, Lock, Unlock, TrendingUp } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const TABS = [
  { id: "coa", label: "Chart of Accounts" },
  { id: "bank", label: "Bank Accounts" },
  { id: "recon", label: "Bank Reconciliation" },
  { id: "budgets", label: "Budgets vs Actual" },
  { id: "tax", label: "Tax Breakdown" },
  { id: "assets", label: "Fixed Assets" },
  { id: "journals", label: "Journal Entries" },
  { id: "close", label: "Period Close" },
];

export default function Finance() {
  const { tenant } = useAuth();
  const [tab, setTab] = useState("coa");

  return (
    <div className="p-4 sm:p-6 lg:p-12 max-w-[1400px]" data-testid="finance-page">
      <PageHeader title="Finance" subtitle="Chart of accounts, bank reconciliation, budgets, tax breakdown, fixed assets, journals & period close." />
      <div className="mt-6 flex flex-wrap gap-1 border-b border-[#DEE2E6]">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`finance-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium -mb-px border-b-2 ${tab === t.id ? "border-[#FF4F00] text-[#0A0A0A]" : "border-transparent text-[#495057] hover:text-[#0A0A0A]"}`}>
            {t.label}
          </button>
        ))}
      </div>
      <div className="mt-6">
        {tab === "coa" && <ChartOfAccounts />}
        {tab === "bank" && <BankAccounts currency={tenant?.currency} />}
        {tab === "recon" && <BankReconciliation currency={tenant?.currency} />}
        {tab === "budgets" && <BudgetsVsActual currency={tenant?.currency} />}
        {tab === "tax" && <TaxBreakdown currency={tenant?.currency} />}
        {tab === "assets" && <FixedAssets currency={tenant?.currency} />}
        {tab === "journals" && <JournalEntries currency={tenant?.currency} />}
        {tab === "close" && <PeriodClose currency={tenant?.currency} />}
      </div>
    </div>
  );
}

/* ----------------------- Chart of Accounts ----------------------- */
const ACCOUNT_TYPES = [
  { id: "asset", label: "Asset" },
  { id: "liability", label: "Liability" },
  { id: "equity", label: "Equity" },
  { id: "income", label: "Income" },
  { id: "expense", label: "Expense" },
  { id: "other", label: "Other" },
];

function ChartOfAccounts() {
  const [rows, setRows] = useState([]);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`/finance/chart-of-accounts?include_inactive=${includeInactive}`).then(({ data }) => setRows(data));
  useEffect(() => { load(); }, [includeInactive]);  // eslint-disable-line react-hooks/exhaustive-deps

  const resetDefaults = async () => {
    if (!window.confirm("Reset Chart of Accounts to defaults? This clears any customizations but preserves all journal entries.")) return;
    setBusy(true);
    try { await api.post("/finance/chart-of-accounts/reset-defaults"); await load(); }
    finally { setBusy(false); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Deactivate account "${row.code}-${row.name}"? It will be hidden from new postings but historic journal entries are preserved.`)) return;
    try { await api.delete(`/finance/chart-of-accounts/${row.id}`); await load(); }
    catch (e) { alert(e?.response?.data?.detail || "Couldn't deactivate"); }
  };

  return (
    <div data-testid="coa-pane">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="text-sm text-[#495057]">
          Each workspace owns its chart. Default 17 system accounts seed automatically; you can add, rename, or deactivate.
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-xs text-[#495057]">
            <input type="checkbox" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} data-testid="coa-show-inactive" />
            Show inactive
          </label>
          <button onClick={resetDefaults} disabled={busy} data-testid="coa-reset-defaults"
            className="text-xs uppercase tracking-[0.15em] border border-[#DEE2E6] hover:border-[#0A0A0A] px-3 py-2">
            Reset defaults
          </button>
          <button onClick={() => { setEditing(null); setOpen(true); }} data-testid="coa-new"
            className="inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#FF4F00] text-white px-4 py-2 text-sm font-medium">
            <Plus size={14} /> New account
          </button>
        </div>
      </div>
      <div className="border border-[#0A0A0A] bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#0A0A0A] text-white text-[11px] uppercase tracking-[0.15em]">
            <tr><Th>Code</Th><Th>Name</Th><Th>Type</Th><Th>System role</Th><Th>Status</Th><Th>Actions</Th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-[#DEE2E6]" data-testid={`coa-row-${r.code}`}>
                <Td className="font-mono">{r.code}</Td>
                <Td>{r.name}</Td>
                <Td className="capitalize">{r.type}</Td>
                <Td className="text-xs text-[#495057]">{r.role || "—"}</Td>
                <Td>
                  <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 ${r.active ? "bg-[#0B7A75]/10 text-[#0B7A75]" : "bg-[#D90429]/10 text-[#D90429]"}`}>
                    {r.active ? "Active" : "Inactive"}
                  </span>
                </Td>
                <Td>
                  <div className="flex gap-2">
                    <button onClick={() => { setEditing(r); setOpen(true); }} data-testid={`coa-edit-${r.code}`}
                      className="text-xs text-[#0A0A0A] hover:text-[#FF4F00] underline">Edit</button>
                    {r.active && (
                      <button onClick={() => remove(r)} data-testid={`coa-del-${r.code}`}
                        className="text-xs text-[#D90429] hover:underline">Deactivate</button>
                    )}
                  </div>
                </Td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-[#495057]">No accounts.</td></tr>}
          </tbody>
        </table>
      </div>
      {open && <COAForm row={editing} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
    </div>
  );
}

function COAForm({ row, onClose, onSaved }) {
  const isEdit = !!row;
  const [f, setF] = useState({
    code: row?.code || "",
    name: row?.name || "",
    type: row?.type || "expense",
    role: row?.role || "",
    active: row?.active !== false,
  });
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async (e) => {
    e.preventDefault(); setErr(""); setSaving(true);
    try {
      const payload = { code: f.code, name: f.name, type: f.type, role: f.role || null, active: f.active };
      if (isEdit) await api.put(`/finance/chart-of-accounts/${row.id}`, payload);
      else await api.post("/finance/chart-of-accounts", payload);
      onSaved();
    } catch (e) { setErr(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  return (
    <Modal title={isEdit ? `Edit ${row.code}-${row.name}` : "New account"} onClose={onClose} testid="coa-modal" wide>
      <form onSubmit={save} className="grid grid-cols-2 gap-4" data-testid="coa-form">
        <Field label="Code" required value={f.code} onChange={(v) => setF({ ...f, code: v })} testid="coa-code" />
        <Field label="Name" required value={f.name} onChange={(v) => setF({ ...f, name: v })} testid="coa-name" />
        <label className="block">
          <span className="block text-xs uppercase tracking-[0.15em] text-[#495057] mb-2">Type</span>
          <select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} data-testid="coa-type"
            className="w-full bg-white border border-[#DEE2E6] px-4 py-3 text-sm focus:border-[#0A0A0A] focus:outline-none">
            {ACCOUNT_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </label>
        <Field label="System role (optional, advanced)" value={f.role} onChange={(v) => setF({ ...f, role: v })} testid="coa-role" />
        <div className="col-span-2 text-xs text-[#495057]">
          The <strong>system role</strong> links this account to auto-postings. Common values: <code>ar</code>, <code>bank</code>, <code>inventory</code>, <code>ap</code>, <code>sales</code>, <code>tax_payable</code>, <code>salaries</code>, <code>cogs</code>. Leave blank for purely informational accounts.
        </div>
        {isEdit && (
          <label className="col-span-2 flex items-center gap-2 text-sm">
            <input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} data-testid="coa-active" />
            Active
          </label>
        )}
        {err && <div className="col-span-2 text-sm text-[#D90429]">{err}</div>}
        <div className="col-span-2 flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-[#DEE2E6]">Cancel</button>
          <button type="submit" disabled={saving} data-testid="coa-save"
            className="px-4 py-2 text-sm bg-[#0A0A0A] hover:bg-[#FF4F00] text-white">Save</button>
        </div>
      </form>
    </Modal>
  );
}

/* ----------------------- Bank Accounts ----------------------- */
function BankAccounts({ currency }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/finance/bank-accounts").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  return (
    <>
      <div className="flex justify-end mb-4">
        <button onClick={() => setOpen(true)} data-testid="add-bank-btn" className="inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#FF4F00] text-white px-4 py-2.5 text-sm rounded-sm"><Plus size={14}/> New bank account</button>
      </div>
      <div className="border border-[#0A0A0A] bg-white overflow-x-auto" data-testid="bank-accounts-table">
        <table className="w-full text-sm">
          <thead className="bg-[#0A0A0A] text-white text-[11px] uppercase tracking-[0.15em]">
            <tr><Th>Name</Th><Th>Bank</Th><Th>Account #</Th><Th>Currency</Th><Th align="right">Opening</Th><Th align="right">Current</Th></tr>
          </thead>
          <tbody>
            {rows.map((b, i) => (
              <tr key={b.id} className="border-b border-[#DEE2E6] hover:bg-[#F8F9FA]" data-testid={`bank-row-${i}`}>
                <Td><div className="font-medium">{b.name}</div></Td>
                <Td className="text-[#495057]">{b.bank_name}</Td>
                <Td className="font-mono text-xs">{b.account_number}</Td>
                <Td>{b.currency}</Td>
                <Td align="right" className="tabular-nums">{currency} {b.opening_balance.toLocaleString()}</Td>
                <Td align="right" className="tabular-nums font-bold">{currency} {b.current_balance.toLocaleString()}</Td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-[#495057]">No bank accounts.</td></tr>}
          </tbody>
        </table>
      </div>
      {open && <BankForm onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
    </>
  );
}

function BankForm({ onClose, onSaved }) {
  const [f, setF] = useState({ name: "", bank_name: "", account_number: "", currency: "GHS", opening_balance: 0 });
  const [err, setErr] = useState("");
  const save = async (e) => { e.preventDefault();
    try { await api.post("/finance/bank-accounts", { ...f, opening_balance: parseFloat(f.opening_balance || 0) }); onSaved(); }
    catch (e) { setErr(e?.response?.data?.detail || "Failed"); } };
  return (
    <Modal title="New bank account" onClose={onClose} testid="bank-modal">
      <form onSubmit={save} className="space-y-4" data-testid="bank-form">
        <Field label="Display name" required value={f.name} onChange={(v) => setF({ ...f, name: v })} testid="bank-name" />
        <Field label="Bank" required value={f.bank_name} onChange={(v) => setF({ ...f, bank_name: v })} testid="bank-bankname" />
        <Field label="Account number" required value={f.account_number} onChange={(v) => setF({ ...f, account_number: v })} testid="bank-acc" />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Currency" value={f.currency} onChange={(v) => setF({ ...f, currency: v })} testid="bank-currency" />
          <Field label="Opening balance" type="number" value={f.opening_balance} onChange={(v) => setF({ ...f, opening_balance: v })} testid="bank-open" />
        </div>
        {err && <div className="text-sm text-[#D90429]">{err}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-[#DEE2E6]">Cancel</button>
          <button type="submit" data-testid="bank-save" className="px-4 py-2 text-sm bg-[#0A0A0A] text-white">Save</button>
        </div>
      </form>
    </Modal>
  );
}

/* ----------------------- Bank Reconciliation ----------------------- */
function BankReconciliation({ currency }) {
  const [accounts, setAccounts] = useState([]);
  const [accId, setAccId] = useState("");
  const [report, setReport] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  useEffect(() => { api.get("/finance/bank-accounts").then(({ data }) => { setAccounts(data); if (data[0]) setAccId(data[0].id); }); }, []);
  const reload = () => accId && api.get(`/finance/bank-reconciliation/${accId}`).then(({ data }) => setReport(data));
  useEffect(() => { reload(); }, [accId]);
  return (
    <>
      <div className="flex items-end justify-between gap-3 mb-4">
        <label className="block min-w-[260px]">
          <span className="block text-xs uppercase tracking-[0.15em] text-[#495057] mb-1.5">Bank account</span>
          <select value={accId} onChange={(e) => setAccId(e.target.value)} data-testid="recon-acc-select"
            className="w-full bg-white border border-[#DEE2E6] px-3 py-2 text-sm">
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.name} · {a.bank_name}</option>)}
          </select>
        </label>
        <button onClick={() => setImportOpen(true)} disabled={!accId} data-testid="import-statement-btn"
          className="inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#FF4F00] text-white px-4 py-2.5 text-sm disabled:opacity-50"><FileText size={14}/> Import statement</button>
      </div>

      {report && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="recon-summary">
            <Kpi label="Imported lines" value={report.summary.imported_lines} />
            <Kpi label="Matched" value={report.summary.matched} tone="good" />
            <Kpi label="Unmatched" value={report.summary.unmatched} tone="warn" />
            <Kpi label="Statement balance" value={`${currency || ""} ${report.summary.statement_balance.toLocaleString()}`} />
          </div>
          <div className="border border-[#0A0A0A] bg-white overflow-x-auto" data-testid="recon-unmatched">
            <table className="w-full text-sm">
              <thead className="bg-[#0A0A0A] text-white text-[11px] uppercase tracking-[0.15em]">
                <tr><Th>Date</Th><Th>Description</Th><Th>Ref</Th><Th align="right">Amount</Th><Th>Status</Th></tr>
              </thead>
              <tbody>
                {report.unmatched.map((l, i) => (
                  <tr key={l.id} className="border-b border-[#DEE2E6]" data-testid={`unmatched-row-${i}`}>
                    <Td className="text-[#495057]">{l.txn_date}</Td>
                    <Td>{l.description}</Td>
                    <Td className="font-mono text-xs text-[#495057]">{l.reference || "—"}</Td>
                    <Td align="right" className={`tabular-nums ${l.amount < 0 ? "text-[#D90429]" : "text-[#0B7A75]"}`}>{l.amount.toLocaleString()}</Td>
                    <Td><span className="text-[10px] uppercase tracking-[0.15em] border border-[#F5B700] bg-[#F5B700]/10 px-2 py-0.5">Unmatched</span></Td>
                  </tr>
                ))}
                {report.unmatched.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-[#0B7A75]">All lines matched. ✓</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}

      {importOpen && <ImportStatementForm accId={accId} onClose={() => setImportOpen(false)} onSaved={() => { setImportOpen(false); reload(); }} />}
    </>
  );
}

function ImportStatementForm({ accId, onClose, onSaved }) {
  const [txt, setTxt] = useState("2026-06-15,Mobile money inflow,Korle Bu,12500\n2026-06-16,Supplier wire MedSupply,SUP-001,-9300");
  const [err, setErr] = useState("");
  const save = async (e) => { e.preventDefault();
    try {
      const lines = txt.split("\n").filter(Boolean).map((line) => {
        const parts = line.split(",").map((p) => p.trim());
        if (parts.length < 3) throw new Error("Need date,description[,ref],amount");
        const amount = parseFloat(parts[parts.length - 1]);
        const reference = parts.length >= 4 ? parts[parts.length - 2] : null;
        return { txn_date: parts[0], description: parts[1], reference, amount };
      });
      await api.post("/finance/bank-statements/import", { bank_account_id: accId, lines });
      onSaved();
    } catch (e) { setErr(e?.response?.data?.detail || e.message); } };
  return (
    <Modal title="Import bank statement" onClose={onClose} testid="import-statement-modal" wide>
      <form onSubmit={save} className="space-y-3" data-testid="import-statement-form">
        <div className="text-xs text-[#495057]">CSV format: <span className="font-mono">date,description,reference,amount</span> per line (reference optional). Negative amount = outflow.</div>
        <textarea value={txt} onChange={(e) => setTxt(e.target.value)} rows={8} data-testid="statement-csv"
          className="w-full font-mono text-xs border border-[#DEE2E6] p-3" />
        {err && <div className="text-sm text-[#D90429]">{err}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-[#DEE2E6]">Cancel</button>
          <button type="submit" data-testid="statement-import-save" className="px-4 py-2 text-sm bg-[#0A0A0A] text-white">Import</button>
        </div>
      </form>
    </Modal>
  );
}

/* ----------------------- Budgets vs Actual ----------------------- */
function BudgetsVsActual({ currency }) {
  const today = new Date();
  const defaultPeriod = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
  const [period, setPeriod] = useState(defaultPeriod);
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => api.get(`/finance/budgets/vs-actual?period=${period}`).then(({ data }) => setData(data));
  useEffect(() => { load(); }, [period]);
  return (
    <>
      <div className="flex items-end justify-between mb-4 gap-3">
        <Field label="Period (YYYY-MM)" value={period} onChange={setPeriod} testid="budget-period" />
        <button onClick={() => setOpen(true)} data-testid="add-budget-btn" className="inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#FF4F00] text-white px-4 py-2.5 text-sm"><Plus size={14}/> New budget line</button>
      </div>
      {data && (
        <div className="border border-[#0A0A0A] bg-white overflow-x-auto" data-testid="budget-table">
          <table className="w-full text-sm">
            <thead className="bg-[#0A0A0A] text-white text-[11px] uppercase tracking-[0.15em]">
              <tr><Th>Category</Th><Th align="right">Budget</Th><Th align="right">Actual</Th><Th align="right">Variance</Th><Th align="right">% Var</Th></tr>
            </thead>
            <tbody>
              {data.rows.map((r, i) => (
                <tr key={r.category} className="border-b border-[#DEE2E6]" data-testid={`budget-row-${i}`}>
                  <Td className="font-medium">{r.category}</Td>
                  <Td align="right" className="tabular-nums">{r.budget.toLocaleString()}</Td>
                  <Td align="right" className="tabular-nums">{r.actual.toLocaleString()}</Td>
                  <Td align="right" className={`tabular-nums ${r.variance < 0 ? "text-[#D90429]" : "text-[#0B7A75]"}`}>{r.variance.toLocaleString()}</Td>
                  <Td align="right" className="tabular-nums">{r.variance_pct == null ? "—" : `${r.variance_pct}%`}</Td>
                </tr>
              ))}
              {data.rows.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-[#495057]">No budgets defined for {period}.</td></tr>}
            </tbody>
            <tfoot className="bg-[#F8F9FA] font-bold">
              <tr><Td>Totals · {currency}</Td>
                <Td align="right" className="tabular-nums">{data.totals.budget.toLocaleString()}</Td>
                <Td align="right" className="tabular-nums">{data.totals.actual.toLocaleString()}</Td>
                <Td colSpan={2} align="right" className="tabular-nums text-[#FF4F00]">{(data.totals.actual - data.totals.budget).toLocaleString()}</Td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
      {open && <BudgetForm onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} defaultPeriod={period} />}
    </>
  );
}

function BudgetForm({ onClose, onSaved, defaultPeriod }) {
  const [f, setF] = useState({ period: defaultPeriod, category: "", amount: 0, notes: "" });
  const [err, setErr] = useState("");
  const save = async (e) => { e.preventDefault();
    try { await api.post("/finance/budgets", { ...f, amount: parseFloat(f.amount || 0) }); onSaved(); }
    catch (e) { setErr(e?.response?.data?.detail || "Failed"); } };
  return (
    <Modal title="New budget line" onClose={onClose} testid="budget-modal">
      <form onSubmit={save} className="space-y-4" data-testid="budget-form">
        <Field label="Period (YYYY-MM)" required value={f.period} onChange={(v) => setF({ ...f, period: v })} testid="bf-period" />
        <Field label="Category" required value={f.category} onChange={(v) => setF({ ...f, category: v })} testid="bf-category" />
        <Field label="Amount" type="number" required value={f.amount} onChange={(v) => setF({ ...f, amount: v })} testid="bf-amount" />
        <Field label="Notes" value={f.notes} onChange={(v) => setF({ ...f, notes: v })} testid="bf-notes" />
        {err && <div className="text-sm text-[#D90429]">{err}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-[#DEE2E6]">Cancel</button>
          <button type="submit" data-testid="budget-save" className="px-4 py-2 text-sm bg-[#0A0A0A] text-white">Save</button>
        </div>
      </form>
    </Modal>
  );
}

/* ----------------------- Tax breakdown ----------------------- */
function TaxBreakdown({ currency }) {
  const today = new Date();
  const def = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;
  const [period, setPeriod] = useState(def);
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/finance/tax/breakdown?period=${period}`).then(({ data }) => setData(data)); }, [period]);
  return (
    <>
      <div className="max-w-xs mb-4"><Field label="Period (YYYY-MM)" value={period} onChange={setPeriod} testid="tax-period" /></div>
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="tax-breakdown">
          <Kpi label="Invoices" value={data.invoice_count} />
          <Kpi label="Subtotal" value={`${currency || ""} ${data.subtotal.toLocaleString()}`} />
          <Kpi label="NHIL 2.5%" value={data.nhil_2_5.toLocaleString()} />
          <Kpi label="GETFund 2.5%" value={data.getfund_2_5.toLocaleString()} />
          <Kpi label="COVID Levy 1%" value={data.covid_1_0.toLocaleString()} />
          <Kpi label="VAT 12.5%" value={data.vat_12_5.toLocaleString()} />
          <Kpi label="Total tax" value={data.total_tax.toLocaleString()} tone="warn" />
          <Kpi label="Grand total" value={`${currency || ""} ${data.grand_total.toLocaleString()}`} tone="good" />
        </div>
      )}
    </>
  );
}

/* ----------------------- Fixed Assets ----------------------- */
function FixedAssets({ currency }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/finance/fixed-assets").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  const postDep = async (id) => { await api.post(`/finance/fixed-assets/${id}/post-depreciation`); load(); };
  return (
    <>
      <div className="flex justify-end mb-4">
        <button onClick={() => setOpen(true)} data-testid="add-asset-btn" className="inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#FF4F00] text-white px-4 py-2.5 text-sm"><Plus size={14}/> New fixed asset</button>
      </div>
      <div className="border border-[#0A0A0A] bg-white overflow-x-auto" data-testid="assets-table">
        <table className="w-full text-sm">
          <thead className="bg-[#0A0A0A] text-white text-[11px] uppercase tracking-[0.15em]">
            <tr><Th>Asset #</Th><Th>Name</Th><Th>Category</Th><Th>Method</Th><Th align="right">Cost</Th><Th align="right">Accum Depn</Th><Th align="right">NBV</Th><Th></Th></tr>
          </thead>
          <tbody>
            {rows.map((a, i) => (
              <tr key={a.id} className="border-b border-[#DEE2E6]" data-testid={`asset-row-${i}`}>
                <Td className="font-mono text-xs">{a.asset_no}</Td>
                <Td><div className="font-medium">{a.name}</div><div className="text-xs text-[#495057]">since {a.purchase_date}</div></Td>
                <Td>{a.category}</Td>
                <Td className="text-xs uppercase tracking-[0.15em] text-[#495057]">{a.method.replace("_", " ")}</Td>
                <Td align="right" className="tabular-nums">{a.cost.toLocaleString()}</Td>
                <Td align="right" className="tabular-nums text-[#D90429]">{currency} {a.depreciation.accumulated_depreciation.toLocaleString()}</Td>
                <Td align="right" className="tabular-nums font-bold">{currency} {a.depreciation.net_book_value.toLocaleString()}</Td>
                <Td><button onClick={() => postDep(a.id)} data-testid={`post-dep-${i}`} className="px-2 py-1 text-xs border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white"><Calculator size={10} className="inline" /> Post depn JE</button></Td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-[#495057]">No fixed assets yet.</td></tr>}
          </tbody>
        </table>
      </div>
      {open && <AssetForm onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
    </>
  );
}

function AssetForm({ onClose, onSaved }) {
  const [f, setF] = useState({ name: "", asset_no: "", category: "Equipment", purchase_date: "", cost: 0, useful_life_months: 60, salvage_value: 0, method: "straight_line", reducing_rate_pct: 20 });
  const [err, setErr] = useState("");
  const save = async (e) => { e.preventDefault();
    try { await api.post("/finance/fixed-assets", { ...f, cost: parseFloat(f.cost), useful_life_months: parseInt(f.useful_life_months), salvage_value: parseFloat(f.salvage_value), reducing_rate_pct: parseFloat(f.reducing_rate_pct) }); onSaved(); }
    catch (e) { setErr(e?.response?.data?.detail || "Failed"); } };
  return (
    <Modal title="New fixed asset" onClose={onClose} testid="asset-modal" wide>
      <form onSubmit={save} className="grid grid-cols-2 gap-4" data-testid="asset-form">
        <Field label="Name" required value={f.name} onChange={(v) => setF({ ...f, name: v })} testid="af-name" />
        <Field label="Asset #" required value={f.asset_no} onChange={(v) => setF({ ...f, asset_no: v })} testid="af-no" />
        <Field label="Category" value={f.category} onChange={(v) => setF({ ...f, category: v })} testid="af-cat" />
        <Field label="Purchase date (YYYY-MM-DD)" required value={f.purchase_date} onChange={(v) => setF({ ...f, purchase_date: v })} testid="af-date" />
        <Field label="Cost" type="number" required value={f.cost} onChange={(v) => setF({ ...f, cost: v })} testid="af-cost" />
        <Field label="Useful life (months)" type="number" required value={f.useful_life_months} onChange={(v) => setF({ ...f, useful_life_months: v })} testid="af-life" />
        <Field label="Salvage value" type="number" value={f.salvage_value} onChange={(v) => setF({ ...f, salvage_value: v })} testid="af-salv" />
        <label className="block">
          <span className="block text-xs uppercase tracking-[0.15em] text-[#495057] mb-1.5">Method</span>
          <select value={f.method} onChange={(e) => setF({ ...f, method: e.target.value })} data-testid="af-method" className="w-full bg-white border border-[#DEE2E6] px-3 py-2 text-sm">
            <option value="straight_line">Straight line</option>
            <option value="reducing_balance">Reducing balance</option>
          </select>
        </label>
        {f.method === "reducing_balance" && (
          <Field label="Reducing rate %" type="number" value={f.reducing_rate_pct} onChange={(v) => setF({ ...f, reducing_rate_pct: v })} testid="af-rate" />
        )}
        {err && <div className="col-span-2 text-sm text-[#D90429]">{err}</div>}
        <div className="col-span-2 flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-[#DEE2E6]">Cancel</button>
          <button type="submit" data-testid="asset-save" className="px-4 py-2 text-sm bg-[#0A0A0A] text-white">Save</button>
        </div>
      </form>
    </Modal>
  );
}

/* ----------------------- Journal Entries ----------------------- */
function JournalEntries({ currency }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/finance/journal-entries").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  return (
    <>
      <div className="flex justify-end mb-4">
        <button onClick={() => setOpen(true)} data-testid="add-journal-btn" className="inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#FF4F00] text-white px-4 py-2.5 text-sm"><Plus size={14}/> New journal entry</button>
      </div>
      <div className="border border-[#0A0A0A] bg-white overflow-x-auto" data-testid="journal-table">
        <table className="w-full text-sm">
          <thead className="bg-[#0A0A0A] text-white text-[11px] uppercase tracking-[0.15em]">
            <tr><Th>#</Th><Th>Date</Th><Th>Memo</Th><Th>Source</Th><Th align="right">Debit</Th><Th align="right">Credit</Th></tr>
          </thead>
          <tbody>
            {rows.map((j, i) => (
              <tr key={j.id} className="border-b border-[#DEE2E6]" data-testid={`journal-row-${i}`}>
                <Td className="font-mono text-xs">{j.number}</Td>
                <Td className="text-[#495057]">{j.posting_date}</Td>
                <Td>{j.memo}</Td>
                <Td className="text-xs uppercase tracking-[0.15em] text-[#495057]">{j.source || "manual"}</Td>
                <Td align="right" className="tabular-nums">{j.total_debit.toLocaleString()}</Td>
                <Td align="right" className="tabular-nums">{j.total_credit.toLocaleString()}</Td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-[#495057]">No journal entries.</td></tr>}
          </tbody>
        </table>
      </div>
      {open && <JournalForm onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
    </>
  );
}

function JournalForm({ onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [f, setF] = useState({ posting_date: today, memo: "", lines: [{ account: "", debit: 0, credit: 0 }, { account: "", debit: 0, credit: 0 }] });
  const [err, setErr] = useState("");
  const update = (idx, k, v) => { const lines = [...f.lines]; lines[idx] = { ...lines[idx], [k]: v }; setF({ ...f, lines }); };
  const addLine = () => setF({ ...f, lines: [...f.lines, { account: "", debit: 0, credit: 0 }] });
  const totals = f.lines.reduce((acc, l) => ({ debit: acc.debit + parseFloat(l.debit || 0), credit: acc.credit + parseFloat(l.credit || 0) }), { debit: 0, credit: 0 });
  const save = async (e) => { e.preventDefault();
    try {
      const lines = f.lines.map((l) => ({ ...l, debit: parseFloat(l.debit || 0), credit: parseFloat(l.credit || 0) }));
      await api.post("/finance/journal-entries", { ...f, lines }); onSaved();
    } catch (e) {
      const d = e?.response?.data?.detail;
      setErr(typeof d === "string" ? d : Array.isArray(d) ? d.map(x => x.msg).join("; ") : "Failed");
    } };
  return (
    <Modal title="New journal entry" onClose={onClose} testid="journal-modal" wide>
      <form onSubmit={save} className="space-y-4" data-testid="journal-form">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Posting date" required value={f.posting_date} onChange={(v) => setF({ ...f, posting_date: v })} testid="je-date" />
          <Field label="Memo" required value={f.memo} onChange={(v) => setF({ ...f, memo: v })} testid="je-memo" />
        </div>
        <div className="border border-[#DEE2E6]">
          <table className="w-full text-sm">
            <thead className="bg-[#F8F9FA] text-[10px] uppercase tracking-[0.15em] text-[#495057]"><tr><Th>Account</Th><Th>Description</Th><Th align="right">Debit</Th><Th align="right">Credit</Th></tr></thead>
            <tbody>
              {f.lines.map((l, i) => (
                <tr key={i} className="border-t border-[#DEE2E6]">
                  <td className="px-2 py-1"><input value={l.account} onChange={(e) => update(i, "account", e.target.value)} data-testid={`je-acc-${i}`} className="w-full border border-[#DEE2E6] px-2 py-1 text-sm" placeholder="e.g. 1000-Cash"/></td>
                  <td className="px-2 py-1"><input value={l.description || ""} onChange={(e) => update(i, "description", e.target.value)} data-testid={`je-desc-${i}`} className="w-full border border-[#DEE2E6] px-2 py-1 text-sm"/></td>
                  <td className="px-2 py-1"><input type="number" value={l.debit} onChange={(e) => update(i, "debit", e.target.value)} data-testid={`je-debit-${i}`} className="w-full border border-[#DEE2E6] px-2 py-1 text-sm text-right tabular-nums"/></td>
                  <td className="px-2 py-1"><input type="number" value={l.credit} onChange={(e) => update(i, "credit", e.target.value)} data-testid={`je-credit-${i}`} className="w-full border border-[#DEE2E6] px-2 py-1 text-sm text-right tabular-nums"/></td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-[#F8F9FA] font-bold">
              <tr><Td colSpan={2}>Totals</Td><Td align="right" className="tabular-nums">{totals.debit.toFixed(2)}</Td><Td align="right" className="tabular-nums">{totals.credit.toFixed(2)}</Td></tr>
            </tfoot>
          </table>
        </div>
        <div className="flex justify-between items-center">
          <button type="button" onClick={addLine} data-testid="je-add-line" className="text-xs text-[#0A0A0A] underline">+ Add line</button>
          <div className={`text-xs ${Math.abs(totals.debit - totals.credit) < 0.01 ? "text-[#0B7A75]" : "text-[#D90429]"}`}>
            {Math.abs(totals.debit - totals.credit) < 0.01 ? "✓ Balanced" : `Unbalanced by ${(totals.debit - totals.credit).toFixed(2)}`}
          </div>
        </div>
        {err && <div className="text-sm text-[#D90429]" data-testid="je-error">{err}</div>}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-[#DEE2E6]">Cancel</button>
          <button type="submit" data-testid="je-save" className="px-4 py-2 text-sm bg-[#0A0A0A] text-white">Post entry</button>
        </div>
      </form>
    </Modal>
  );
}

/* ----------------------- Period Close ----------------------- */
function PeriodClose({ currency }) {
  const [rows, setRows] = useState([]);
  const [period, setPeriod] = useState("");
  const [err, setErr] = useState("");
  const [view, setView] = useState(null);
  const load = () => api.get("/finance/period-closes").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  const close = async (e) => { e.preventDefault(); setErr("");
    try { await api.post("/finance/period-closes", { period }); setPeriod(""); load(); }
    catch (e) { setErr(e?.response?.data?.detail || "Failed"); } };
  const reopen = async (p) => { await api.post(`/finance/period-closes/${p}/reopen`); load(); };
  return (
    <>
      <form onSubmit={close} className="flex items-end gap-3 mb-4" data-testid="period-close-form">
        <Field label="Period to close (YYYY-MM)" value={period} onChange={setPeriod} testid="close-period" />
        <button type="submit" disabled={!period} data-testid="close-period-btn" className="inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#FF4F00] text-white px-4 py-2.5 text-sm disabled:opacity-50"><Lock size={14}/> Close period</button>
        {err && <div className="text-sm text-[#D90429]">{err}</div>}
      </form>
      <div className="border border-[#0A0A0A] bg-white overflow-x-auto" data-testid="closes-table">
        <table className="w-full text-sm">
          <thead className="bg-[#0A0A0A] text-white text-[11px] uppercase tracking-[0.15em]">
            <tr><Th>Period</Th><Th>Closed at</Th><Th align="right">Total debit</Th><Th align="right">Total credit</Th><Th></Th></tr>
          </thead>
          <tbody>
            {rows.map((c, i) => (
              <tr key={c.id} className="border-b border-[#DEE2E6]" data-testid={`close-row-${i}`}>
                <Td className="font-mono">{c.period}</Td>
                <Td className="text-xs text-[#495057]">{new Date(c.closed_at).toLocaleString()}</Td>
                <Td align="right" className="tabular-nums">{c.total_debit.toLocaleString()}</Td>
                <Td align="right" className="tabular-nums">{c.total_credit.toLocaleString()}</Td>
                <Td>
                  <div className="flex gap-1">
                    <button onClick={() => setView(c)} className="px-2 py-1 text-xs border border-[#0A0A0A]">Trial balance</button>
                    <button onClick={() => reopen(c.period)} data-testid={`reopen-${c.period}`} className="px-2 py-1 text-xs border border-[#D90429] text-[#D90429] inline-flex items-center gap-1"><Unlock size={10}/> Reopen</button>
                  </div>
                </Td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-[#495057]">No closed periods.</td></tr>}
          </tbody>
        </table>
      </div>
      {view && (
        <Modal title={`Trial balance · ${view.period}`} onClose={() => setView(null)} testid="trial-balance-modal" wide>
          <div className="overflow-x-auto" data-testid="trial-balance">
            <table className="w-full text-sm border border-[#DEE2E6]">
              <thead className="bg-[#F8F9FA] text-[10px] uppercase tracking-[0.15em] text-[#495057]">
                <tr><Th>Account</Th><Th align="right">Debit</Th><Th align="right">Credit</Th><Th align="right">Net</Th></tr>
              </thead>
              <tbody>
                {(view.trial_balance || []).map((t, i) => (
                  <tr key={i} className="border-t border-[#DEE2E6]">
                    <Td>{t.account}</Td>
                    <Td align="right" className="tabular-nums">{t.debit.toLocaleString()}</Td>
                    <Td align="right" className="tabular-nums">{t.credit.toLocaleString()}</Td>
                    <Td align="right" className={`tabular-nums ${t.net < 0 ? "text-[#D90429]" : "text-[#0B7A75]"}`}>{t.net.toLocaleString()}</Td>
                  </tr>
                ))}
                {(view.trial_balance || []).length === 0 && <tr><td colSpan={4} className="p-8 text-center text-[#495057]">No journal entries posted in this period.</td></tr>}
              </tbody>
            </table>
          </div>
        </Modal>
      )}
    </>
  );
}

/* ----------------------- Shared mini KPI tile ----------------------- */
function Kpi({ label, value, tone }) {
  const cls = tone === "good" ? "border-[#0B7A75]" : tone === "warn" ? "border-[#F5B700]" : "border-[#DEE2E6]";
  return (
    <div className={`border bg-white p-4 ${cls}`} data-testid={`kpi-${(label || "").toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="text-[10px] uppercase tracking-[0.15em] text-[#495057]">{label}</div>
      <div className="mt-1 text-xl font-bold tabular-nums">{value}</div>
    </div>
  );
}
